import i18next from 'i18next';
import moment, {type Moment} from 'moment-timezone';

import {type LeaveSummary} from 'web-app/react/profile/leave-widget/reducer';
import {roundToDecimals} from 'web-app/util/math';
import {hasValue} from 'web-app/util/typescript';
import {printDuration} from 'web-app/react/staffing-attendance/utils/moment/print-duration';
import {decimalHoursToTimeMap} from 'web-app/react/staffing-attendance/utils/decimal-time-converter';

import {type DurationType, LeaveDurationType} from './leave-from-api-adapter';

export const createYearString = (leave: ReturnType<typeof LeaveSummary>): string => {
    const fromYear = String(moment(leave.period.from).year());
    const toYear = String(moment(leave.period.to).year());

    if (fromYear === toYear) {
        return fromYear;
    } else {
        return `${fromYear}/${toYear}`;
    }
};

export const createDefaultSelectedYear = (leaves: Array<ReturnType<typeof LeaveSummary>>): string => {
    const currentYear = String(moment().year());
    const leave = leaves.find(leave => isCurrentPeriod(leave));
    if (leave) {
        return createYearString(leave);
    } else {
        return currentYear;
    }
};

export const isCurrentPeriod = (leave: ReturnType<typeof LeaveSummary>): boolean =>
    // The weird brackets at the end signifies wether the range should be inclusive or exclusive
    // Since we get a inclusive dataset from the backend we have to use the [] (moment defaults to
    // exclusive)
    // if this code is ran on the exact day of leave.period.to ---> for example 2022-08-31:12:34:00
    // this condition will be false as leave.period.to ----> is 2022-08-31:00:00:00
    // hence we're adding a endOf('day') cast
    // to make sure that leave.period.to is ---> 2022-08-31:23:59:59
    moment().isBetween(leave.period.from, moment(leave.period.to).endOf('day'), undefined, '[]');

// this is the first part of the string
// returns 23 Aug - 25 Aug for example
export const renderRange = ({startDate, endDate}: {startDate: Moment; endDate: Moment | null}) => {
    // only render the end date if the first day and the last day are different
    const renderEndDate = hasValue(endDate) && hasValue(startDate) && endDate.isAfter(startDate, 'day');

    if (renderEndDate) {
        return {
            translatedString: `${i18next.t('leaveRegistration.subLeaves.partDayFromTo', {
                startTime: startDate.format('LL'),
                endTime: endDate.format('LL'),
            })} - `,
            untranslatedString: `${startDate.format('LL')} - ${endDate.format('LL')}`,
        };
    }
    return {translatedString: `${startDate.format('LL')} - `, untranslatedString: `${startDate.format('LL')}`};
};
/**
 * this takes care of rendering the duration for the employee leaves
 * it also supports the PDL 
 * @param param0 
 * @returns translatedString: which the actual string, count: number, the value of days or null
 * the translatedString is there because JEST has issues with i18next, so we check the count instead
 * to support unit tests for this function
 
 */
export const renderEmployeeDuration = ({
    startDate,
    endDate,
    duration,
    isPartDayLeaveEnabled,
    isPDL,
}: {
    startDate: Moment;
    endDate: Moment | null;
    duration: DurationType;
    isPartDayLeaveEnabled: boolean;
    isPDL: boolean;
}): {translatedString: string; count: number; hourRange?: string} | null => {
    // only show the hour range if the leave is a PDL leave
    const hourRange =
        isPDL && startDate.isSame(endDate, 'day') ? `- ${startDate.format('LT')} - ${endDate?.format('LT')}` : '';

    const countInDays = roundToDecimals(duration.duration.days, 2);
    const countInHours = duration.duration.hours ?? null;

    // if the leave is a PDL leave
    // always show the value in hours
    if (isPDL && hasValue(countInHours)) {
        return {
            translatedString: `(${printDuration(moment.duration(decimalHoursToTimeMap(countInHours)))}) ${hourRange}`,
            count: countInHours,
            hourRange,
        };
    }

    // switch on the feature enabled, we can have the following scenarios
    // ----------------------------------------------------------
    // PDL -> on -> Full day leave with no hours information (legacy)
    // PDL -> on -> Full day leave with hours information
    // ----------------------------------------------------------
    // PDL -> off -> Full day leaves with/without hours -> we only want to show the days in that case (except if the leave is a PDL leave by nature)
    // ----------------------------------------------------------
    if (isPartDayLeaveEnabled) {
        // if PDL is enabled
        // PDL -> on -> Full day leave with hours information
        if (duration.type === LeaveDurationType.HOURS && hasValue(countInHours)) {
            return {
                translatedString: `${i18next.t('day', {count: countInDays})} (${printDuration(
                    moment.duration(moment.duration(decimalHoursToTimeMap(countInHours))),
                )}) ${hourRange}`,
                count: countInHours,
                hourRange,
            };
        }
        // if PDL is enabled
        // PDL -> on -> Full day leave with no hours information (legacy)
        if (duration.type === LeaveDurationType.ABSTRACT_DAYS) {
            return {
                translatedString: `(${i18next.t('day', {count: countInDays})})`,
                count: countInDays,
            };
        }
    }

    // in any other case we return days
    return {
        translatedString: `(${i18next.t('day', {count: countInDays})})`,
        count: countInDays,
    };
};

/**
 *
 * // this is the first part of the string used in the child leave widget
 * returns 23 Aug
 * returns 23 Aug - 25 Aug
 * this function does not need to support PDL
 * @returns translatedString: which the actual string, count: number, the value of days or null
 * the translatedString is there because JEST has issues with i18next, so we check the count instead
 * to support unit tests for this function
 */
export const renderChildDuration = (
    duration: DurationType | number | null,
): {translatedString: string; count: number} | null => {
    // Normal child leaves can have this`duration: DurationType`
    // we also check here if the type is ABSTRACT_DAYS just to make sure that we don't have any leaks
    if (hasValue(duration) && typeof duration === 'object' && duration.type === LeaveDurationType.ABSTRACT_DAYS) {
        return {
            translatedString: `(${i18next.t('day', {count: duration.duration.days, days: duration.duration.days})})`,
            count: duration.duration.days,
        };
    }

    // Vacation replies has type `duration: number | null`
    if (hasValue(duration) && typeof duration === 'number') {
        return {translatedString: `(${i18next.t('day', {count: duration, days: duration})})`, count: duration};
    }

    // This is an impossible scenario, the functions returns that in case
    // I however don't want to return {translatedString: '', count: 0}
    return null;
};

/**
 * Used in the selectors to build the chart
 * @param selectedPeriod
 * @param isPDLEnabled
 * @returns
 */
export function mapSelectedPeriodData(selectedPeriod: any, isPDLEnabled: boolean) {
    // this is why we access the data as we do here
    // this is definitely spaghetti code but I'm good at making pasta
    // "`-._,-'"`-._,-'"`-._,-'"`-._,-'
    const accessor = isPDLEnabled ? 'minutes' : 'days';
    const absence = selectedPeriod.months.map(month => month?.[accessor]?.ABSENT ?? month?.ABSENT ?? 0);
    const sickness = selectedPeriod.months.map(month => month?.[accessor]?.SICK ?? month?.SICK ?? 0);
    const vacation = selectedPeriod.months.map(month => month?.[accessor]?.VACATION ?? month?.VACATION ?? 0);
    const childSick = selectedPeriod.months.map(month => month?.[accessor]?.CHILD_SICK ?? month?.CHILD_SICK ?? 0);
    return [absence, sickness, vacation, childSick];
}
